import React, { useState, useEffect } from 'react';
import SectionCard from '../components/SectionCard.jsx';
import ScenarioPanel from '../components/ScenarioPanel.jsx';
import DriverPanel from '../components/DriverPanel.jsx';
import InflationPanel from '../components/InflationPanel.jsx';
import SeasonalEditor from '../components/SeasonalEditor.jsx';
import PiecewiseEditor from '../components/PiecewiseEditor.jsx';
import PiecewiseConfigurator from '../components/PiecewiseConfigurator.jsx';
import ProfileManager from '../components/ProfileManager.jsx';
import AccountVisibilityPanel from '../components/AccountVisibilityPanel.jsx';
import GuideControlsPanel from '../components/GuideControlsPanel.jsx';
import { makeShowBlock, SubViewBreadcrumb } from '../components/SubViewShell.jsx';
import PiecewiseScheduleEditor from '../components/PiecewiseScheduleEditor.jsx';
// Phase 1.5 — removed vendor-specific mirror imports (data now flows via the
// atomic-ledger-contract; no local vendor snapshot in Lens).
import { RECURRING, INCOMES, DASHBOARD_VERSION, SNAPSHOT_AS_OF, MORTGAGES } from '../data.js';
import {
  formatBookScopePostureLabel,
  formatBookScopePostureTooltip,
  formatLedgerBookSettingsSummary,
  formatLedgerBookSettingsTooltip,
  formatSourceStateLabel,
  formatSourceStateTooltip,
} from '../source-state.js';
import {
  recurringKey,
  resolveSchedule, fmtMoney,
} from '../engine.js';
import {
  fallbackCurrencyDataPlaneContract,
  getCurrencyDataPlaneContract,
  getDataPlaneUrl,
} from '../data-plane.js';

const TAB_CONTENT = 'flex flex-col [&>*+*]:mt-4';
const BTN_GHOST_SM = 'bg-transparent text-accent-blue border border-dashed border-border-strong px-2 py-[2px] rounded-sm font-[inherit] text-[11px] cursor-pointer hover:border-accent-blue hover:bg-[rgba(96,165,250,0.08)]';
const COMPACT_LIST = 'mt-[0.6rem] mb-0 pl-[1.1rem] text-text-muted text-[0.84rem] leading-[1.65]';
const HINT = 'mt-[0.7rem] text-[0.80rem] text-text-muted px-3 py-[0.55rem] bg-surface-el border-l-[3px] border-border-strong rounded-sm';
const SETTINGS_GRID = 'grid grid-cols-[repeat(auto-fit,minmax(260px,1fr))] gap-[14px]';
const SETTINGS_CELL = 'p-3 bg-white/[0.02] border border-border rounded-[8px]';
const SETTINGS_LABEL = 'block text-[11px] uppercase tracking-[0.07em] text-text-muted mb-[6px]';
const SETTINGS_PILLS = 'flex flex-wrap gap-[6px] mb-1';
const CFA_SETTINGS_GRID = 'grid grid-cols-[repeat(auto-fit,minmax(320px,1fr))] gap-4 items-start';
const PILL_BASE = 'inline-flex items-center text-[0.72rem] px-[9px] py-[3px] rounded-xl bg-surface-raised text-text-muted border border-border tracking-[0.04em] font-[inherit] transition-all duration-150 cursor-pointer hover:text-text hover:border-border-strong';
const PILL_ACTIVE = '!text-white !bg-accent-blue !border-accent-blue';
const PM_BTN = 'bg-transparent border border-border-strong text-text-muted px-[0.7rem] py-[0.4rem] rounded-sm cursor-pointer font-[inherit] text-[0.78rem] hover:text-text hover:border-accent-gold';
const GROUP_LABEL = 'text-[0.75rem] font-semibold uppercase tracking-[0.08em] text-text-muted mt-6 mb-2 pb-1 border-b border-white/[0.06] first:mt-0';

const COMPARISON_MODES = [
  { id: 'none',    label: 'Off',         hint: 'No comparison column' },
  { id: 'mom',     label: 'Month-on-Month', hint: 'Lag = 1 month' },
  { id: 'qoq',     label: 'Quarter-on-Quarter', hint: 'Lag = 3 months' },
  { id: 'yoy',     label: 'Year-on-Year',   hint: 'Lag = 12 months' },
  { id: 'hybrid',  label: 'Hybrid (MoM + QoQ + YoY)', hint: 'All three deltas side-by-side' },
  { id: 'all',     label: 'All-time stats',   hint: 'Min · max · mean · stdev across horizon' },
];

const ORIENTATION_OPTS = [
  { id: 'horizontal', label: 'Dates across (default)', hint: 'Months as columns → horizontal date axis (natural financial layout)' },
  { id: 'vertical',   label: 'Dates down',              hint: 'Months as rows → vertical scanning by period' },
];

const STATEMENT_STYLES = [
  { id: 'analytical', label: 'Analytical (default)', hint: 'Wide multi-column grid' },
  { id: 'formal',     label: 'Formal (filing-style)', hint: 'Single amount column with rules' },
];

function Section({ title, subtitle, children, defaultOpen = true, forceOpen = false, sectionId }) {
  const [open, setOpen] = useState(defaultOpen);
  /* v8 Path H — when a submodule routes here (forceOpen becomes true),
     expand automatically so users don't land on a collapsed shell. User
     can still manually collapse afterward; we only force-open on the
     false→true edge, not on every render. */
  useEffect(() => {
    if (forceOpen) setOpen(true);
  }, [forceOpen]);
  /* v8 Path K — forward sectionId to SectionCard so sub-nav scroll lands
     on this card's title (accounting for the sticky topbar via CSS
     scroll-margin-top). */
  return (
    <SectionCard title={title} subtitle={subtitle} sectionId={sectionId}>
      <button className={BTN_GHOST_SM} onClick={() => setOpen(o => !o)}>
        {open ? '▾ Collapse' : '▸ Expand'}
      </button>
      {open && <div className="pt-[10px]">{children}</div>}
    </SectionCard>
  );
}

function CurrencyDataSpinePanel({ state, onRefresh }) {
  const contract = state.contract || fallbackCurrencyDataPlaneContract();
  const statusLabel = state.status === 'ready'
    ? 'source-wired'
    : state.status === 'checking'
      ? 'checking'
      : 'contract held';
  return (
    <div className={SETTINGS_CELL}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <label className={SETTINGS_LABEL}>Central source</label>
          <div className={SETTINGS_PILLS}>
            <span className={PILL_BASE}>data.cleic.com</span>
            <span className={PILL_BASE}>{contract.provider.primary}</span>
            <span className={PILL_BASE}>provider {contract.readiness.providerLiveReadback}</span>
            <span className={PILL_BASE}>{statusLabel}</span>
          </div>
          <p className={HINT}>
            Lens reads the same currency governance contract as Ledger. It can use Data for
            currency context, actuals back-testing context, and model presentation while posted
            accounting truth stays in Ledger.
          </p>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {contract.consumers.map((consumer) => (
              <span
                key={consumer.app}
                className="rounded-sm border border-border bg-surface-raised px-2 py-1 text-[11px] text-text-muted"
                title={consumer.use}
              >
                {consumer.app}
              </span>
            ))}
          </div>
          {state.error && (
            <p className="mt-2 text-[11px] text-accent-gold">{state.error}</p>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          <button type="button" className={BTN_GHOST_SM} onClick={onRefresh}>
            Refresh
          </button>
          <a className={BTN_GHOST_SM} href={getDataPlaneUrl('/')}>
            Open Data
          </a>
        </div>
      </div>
    </div>
  );
}

/* Proposal 5A (2026-04-25) — sidebar split: app-behavior controls vs
   user-parameter data. Settings keeps the universal toggles (model
   state, profiles, visibility, guides, presentation, snapshot). Drivers
   gets the user's own data layer (recurring, model drivers, piecewise).

   Both modules render through this single component via `moduleMode`
   so the section logic + handlers stay unified — F-023 multi-tenancy
   will eventually scope Drivers to a Book; Settings stays user-scoped. */
const SETTINGS_SECTIONS = new Set(['model-assumptions', 'profiles', 'visibility', 'guides', 'presentation', 'snapshot']);
const DRIVERS_SECTIONS  = new Set(['recurring', 'model-drivers', 'piecewise']);

export default function SettingsTab({ scenario, setScenario, activeSection, onBackToOverview, moduleMode = 'settings' }) {
  const show = makeShowBlock(activeSection);
  const overrides = scenario.piecewiseOverrides || {};
  /* Section gate based on which sidebar parent routed here. Each section's
     existing focus-mode `show()` check stays intact; this gate just hides
     sections that belong to the OTHER module. */
  const sourceStateLabel = formatSourceStateLabel(scenario._ledgerSourceState);
  const sourceStateTip = formatSourceStateTooltip(scenario._ledgerSourceState);
  const bookSettingsLabel = formatLedgerBookSettingsSummary(scenario._ledgerSourceState);
  const bookSettingsTip = formatLedgerBookSettingsTooltip(scenario._ledgerSourceState);
  const bookScopeLabel = formatBookScopePostureLabel(scenario._ledgerSourceState);
  const bookScopeTip = formatBookScopePostureTooltip(scenario._ledgerSourceState);
  const [dataPlaneState, setDataPlaneState] = useState({
    status: 'checking',
    contract: fallbackCurrencyDataPlaneContract(),
    error: null,
  });
  const inMode = (sectionId) => {
    if (moduleMode === 'drivers') return DRIVERS_SECTIONS.has(sectionId);
    return SETTINGS_SECTIONS.has(sectionId);
  };

  const refreshDataPlaneContract = () => {
    setDataPlaneState((state) => ({ ...state, status: 'checking', error: null }));
    getCurrencyDataPlaneContract()
      .then((contract) => {
        setDataPlaneState({ status: 'ready', contract, error: null });
      })
      .catch((error) => {
        setDataPlaneState({
          status: 'held',
          contract: fallbackCurrencyDataPlaneContract(),
          error: error instanceof Error ? error.message : String(error),
        });
      });
  };

  useEffect(() => {
    let cancelled = false;
    getCurrencyDataPlaneContract()
      .then((contract) => {
        if (!cancelled) setDataPlaneState({ status: 'ready', contract, error: null });
      })
      .catch((error) => {
        if (!cancelled) {
          setDataPlaneState({
            status: 'held',
            contract: fallbackCurrencyDataPlaneContract(),
            error: error instanceof Error ? error.message : String(error),
          });
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  /* v8 Path G — scroll-to-section when sidebar routes to a sub-item. */
  useEffect(() => {
    if (!activeSection || typeof document === 'undefined') return;
    const t = setTimeout(() => {
      const el = document.getElementById(`section-${activeSection}`) || document.getElementById(activeSection);
      if (!el) return;
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      el.classList.remove('section-focused');
      void el.offsetWidth;
      el.classList.add('section-focused');
      setTimeout(() => el.classList.remove('section-focused'), 1900);
    }, 60);
    return () => clearTimeout(t);
  }, [activeSection]);
  /* Proposal 5B — the piecewise editor surface is now unified inside
     <PiecewiseConfigurator />. SettingsTab keeps the legacy `setOverride`
     helper because non-piecewise sections (Recurring Schedules below)
     still use the same piecewiseOverrides keyspace. */
  const setOverride = (lineKey, schedule) => {
    const next = { ...overrides };
    if (schedule == null) delete next[lineKey];
    else next[lineKey] = schedule;
    setScenario({ ...scenario, piecewiseOverrides: next });
  };

  const setMode = (k, v) => setScenario({ ...scenario, [k]: v });

  return (
    <div className={TAB_CONTENT}>
      <SubViewBreadcrumb moduleId={moduleMode} activeSection={activeSection} onBack={onBackToOverview} />
      {inMode('model-assumptions') && show('overview', 'model-assumptions') && (
      <SectionCard
        sectionId="model-assumptions"
        title="Shared model assumptions · finance · revenue · cost · expense · operations"
        subtitle="Tune reusable finance, revenue, cost, expense, and operations assumptions from the canonical settings surface."
      >
        <ScenarioPanel scenario={scenario} setScenario={setScenario} />
      </SectionCard>
      )}

      {inMode('model-assumptions') && show('overview', 'model-assumptions') && (
      <SectionCard
        sectionId="currency-data-spine"
        title="Currency Data Spine"
        subtitle="Central Data governs currency metadata and rate posture for Ledger and Lens."
      >
        <CurrencyDataSpinePanel state={dataPlaneState} onRefresh={refreshDataPlaneContract} />
      </SectionCard>
      )}

      {inMode('profiles') && show('overview', 'profiles') && (
      <SectionCard
        sectionId="profiles"
        title="Model Profiles"
        subtitle="Private browser JSON only — save, load, duplicate, export, or import local model states."
      >
        <ProfileManager scenario={scenario} setScenario={setScenario} />
      </SectionCard>
      )}

      {inMode('visibility') && show('overview', 'visibility') && (
      <SectionCard
        sectionId="visibility"
        title="Account Visibility"
        subtitle="Every account in the selected Chart of Accounts. Low-signal accounts hide by default — toggle them on to surface them in every module."
      >
        <AccountVisibilityPanel scenario={scenario} setScenario={setScenario} />
      </SectionCard>
      )}

      {inMode('guides') && show('overview', 'guides') && (
      <SectionCard
        sectionId="guides"
        title="Guide Controls"
        subtitle="Turn on/off every layer of guidance across the app — rich tooltips, inline popovers, drill-downs, modified badges, trend sparklines. Off = denser, lower-assistance view."
      >
        <GuideControlsPanel scenario={scenario} setScenario={setScenario} />
      </SectionCard>
      )}

      {inMode('recurring') && show('overview', 'recurring') && (
      <Section
        sectionId="recurring"
        title="Recurring Schedules"
        subtitle="Piecewise month-range editor for any recurring line — subscriptions, primary income, term debt, dedicated fees. Start/stop/step-changes. Model-profile-scoped overrides; durable only via Model Profile save."
        defaultOpen={false}
        forceOpen={activeSection === 'recurring'}
      >
        <div>
          <PiecewiseScheduleEditor
            code={INCOMES.jmPayDirect.code}
            label="Custody stream"
            lineDefault={INCOMES.jmPayDirect.defaultSchedule}
            scenario={scenario} setScenario={setScenario}
          />
          {MORTGAGES.filter(m => m.schedule).map((m) => (
            <PiecewiseScheduleEditor
              key={m.name}
              code={`MORTGAGE-${m.name}`}
              label={`${m.name} mortgage`}
              lineDefault={m.schedule}
              scenario={scenario} setScenario={setScenario}
            />
          ))}

          <h5 className={GROUP_LABEL}>Monthly</h5>
          {RECURRING.monthly.map((r) => (
            <PiecewiseScheduleEditor
              key={r.code + r.vendor}
              code={r.code}
              label={`${r.code} · ${r.vendor}`}
              lineDefault={r.schedule ?? { steps: [{ fromYM: '2026-01', amount: r.amount, note: 'Flat rate' }] }}
              fxInfo={r.fx ? { currency: r.fx.currency, baseRate: r.fx.baseRate, monthlyDrift: r.fx.monthlyDrift ?? 0 } : null}
              scenario={scenario} setScenario={setScenario}
            />
          ))}

          {RECURRING.yearly && RECURRING.yearly.length > 0 && (
            <>
              <h5 className={GROUP_LABEL}>Yearly</h5>
              {RECURRING.yearly.map((r) => (
                <PiecewiseScheduleEditor
                  key={r.code + r.vendor}
                  code={r.code}
                  label={`${r.code} · ${r.vendor} (annual · month ${r.month})`}
                  lineDefault={r.schedule ?? { steps: [{ fromYM: `2026-${String(r.month).padStart(2, '0')}`, amount: r.amount, note: 'Annual flat' }] }}
                  fxInfo={r.fx ? { currency: r.fx.currency, baseRate: r.fx.baseRate, monthlyDrift: r.fx.monthlyDrift ?? 0 } : null}
                  scenario={scenario} setScenario={setScenario}
                />
              ))}
            </>
          )}

          {RECURRING.quarterly && RECURRING.quarterly.length > 0 && (
            <>
              <h5 className={GROUP_LABEL}>Quarterly</h5>
              {RECURRING.quarterly.map((r) => (
                <PiecewiseScheduleEditor
                  key={r.code + r.vendor}
                  code={r.code}
                  label={`${r.code} · ${r.vendor} (quarterly)`}
                  lineDefault={r.schedule ?? { steps: [{ fromYM: '2026-01', amount: r.amount, note: 'Quarterly flat' }] }}
                  fxInfo={r.fx ? { currency: r.fx.currency, baseRate: r.fx.baseRate, monthlyDrift: r.fx.monthlyDrift ?? 0 } : null}
                  scenario={scenario} setScenario={setScenario}
                />
              ))}
            </>
          )}
        </div>
      </Section>
      )}

      {inMode('presentation') && show('overview', 'presentation') && (
      <Section
        sectionId="presentation"
        title="Presentation"
        subtitle="How tables and statements render across Actuals/Budgets/Forecasts"
        defaultOpen={true}
        forceOpen={activeSection === 'presentation'}
      >
        <div className={SETTINGS_GRID}>
          <div className={SETTINGS_CELL}>
            <label className={SETTINGS_LABEL}>Table orientation</label>
            <div className={SETTINGS_PILLS}>
              {ORIENTATION_OPTS.map((o) => (
                <button
                  key={o.id}
                  type="button"
                  className={`${PILL_BASE} ${scenario.tableOrientation === o.id ? PILL_ACTIVE : ''}`}
                  onClick={() => setMode('tableOrientation', o.id)}
                  title={o.hint}
                >
                  {o.label}
                </button>
              ))}
            </div>
            <p className={HINT}>{ORIENTATION_OPTS.find(o => o.id === scenario.tableOrientation)?.hint}</p>
          </div>

          <div className={SETTINGS_CELL}>
            <label className={SETTINGS_LABEL}>Statement style</label>
            <div className={SETTINGS_PILLS}>
              {STATEMENT_STYLES.map((o) => (
                <button
                  key={o.id}
                  type="button"
                  className={`${PILL_BASE} ${scenario.statementStyle === o.id ? PILL_ACTIVE : ''}`}
                  onClick={() => setMode('statementStyle', o.id)}
                  title={o.hint}
                >
                  {o.label}
                </button>
              ))}
            </div>
            <p className={HINT}>{STATEMENT_STYLES.find(o => o.id === scenario.statementStyle)?.hint}</p>
          </div>

          <div className={SETTINGS_CELL}>
            <label className={SETTINGS_LABEL}>Period comparison</label>
            <div className={SETTINGS_PILLS}>
              {COMPARISON_MODES.map((o) => (
                <button
                  key={o.id}
                  type="button"
                  className={`${PILL_BASE} ${scenario.comparisonMode === o.id ? PILL_ACTIVE : ''}`}
                  onClick={() => setMode('comparisonMode', o.id)}
                  title={o.hint}
                >
                  {o.label}
                </button>
              ))}
            </div>
            <p className={HINT}>
              {COMPARISON_MODES.find(o => o.id === scenario.comparisonMode)?.hint}
              {' · '}Applied to Forecasts and IS/BS/CF tables.
            </p>
          </div>
        </div>
      </Section>
      )}

      {/* Proposal 5A renamed sectionId 'drivers' to avoid the collision with
          the Drivers parent module. v2026-05-16 widens the surface to
          `model-drivers` and removes the old narrow route. */}
      {inMode('model-drivers') && show('overview', 'model-drivers') && (
      <Section
        sectionId="model-drivers"
        title="Universal model drivers"
        subtitle="Reusable drivers for finance, revenue, cost, expense, operations, macro, seasonal, top-down, bottom-up, and triangulated models."
        defaultOpen={false}
        forceOpen={activeSection === 'model-drivers'}
      >
        <div className={CFA_SETTINGS_GRID}>
          <DriverPanel scenario={scenario} setScenario={setScenario} />
          <InflationPanel scenario={scenario} setScenario={setScenario} />
          <SeasonalEditor scenario={scenario} setScenario={setScenario} />
        </div>
      </Section>
      )}

      {/* Proposal 5B — unified Piecewise configurator. Replaces four
          previously hardcoded children (piecewise-family / piecewise-subs /
          piecewise-assets / piecewise-ltls) with a single screen where
          users pick a starter template (Household / SMB Service /
          SMB Product) and inline-rename the four engine kinds. Legacy
          activeSection values still route here so old bookmarks survive. */}
      {inMode('piecewise') && show('overview', 'piecewise', 'piecewise-family', 'piecewise-subs', 'piecewise-assets', 'piecewise-ltls') && (
      <Section
        sectionId="piecewise"
        title="Model schedules and pins"
        subtitle="One screen for all recurring schedules, operating cadences, NC-Asset value pins, and NC-Liability outstanding-balance pins. Pick a starter template to seed labels across the enterprise model stack, then rename each category inline."
        defaultOpen={true}
        forceOpen={
          activeSection === 'piecewise' ||
          activeSection === 'piecewise-family' ||
          activeSection === 'piecewise-subs' ||
          activeSection === 'piecewise-assets' ||
          activeSection === 'piecewise-ltls'
        }
      >
        <PiecewiseConfigurator scenario={scenario} setScenario={setScenario} />
      </Section>
      )}

      {/* Phase 1.5 — the vendor mirror settings section was removed. Data now
          flows through the atomic-ledger-contract via Cleic Ledger. */}

      {inMode('snapshot') && show('overview', 'snapshot') && (
      <SectionCard
        sectionId="snapshot"
        title="Snapshot reference"
        subtitle="Read-only — what's loaded right now"
      >
        <ul className={COMPACT_LIST}>
          <li><strong className="text-text">Version:</strong> v{DASHBOARD_VERSION.replace(/^v/, '')}</li>
          <li><strong className="text-text">Snapshot date:</strong> {SNAPSHOT_AS_OF}</li>
          <li><strong className="text-text">Active model profile:</strong> {scenario.name ?? 'Custom'}</li>
          <li><strong className="text-text">Active comparison:</strong> {scenario.comparisonMode ?? 'none'}</li>
          <li><strong className="text-text">Source state:</strong> <span title={sourceStateTip}>{sourceStateLabel}</span></li>
          <li><strong className="text-text">Book settings:</strong> <span title={bookSettingsTip}>{bookSettingsLabel}</span></li>
          <li><strong className="text-text">Book scope:</strong> <span title={bookScopeTip}>{bookScopeLabel}</span></li>
          <li><strong className="text-text">Strict real-book claim:</strong> {scenario._ledgerSourceState?.canClaimStrictRealBook ? 'available inside private contract source; public/client claims still HOLD' : 'HOLD'}</li>
          <li><strong className="text-text">Piecewise overrides:</strong> {Object.keys(overrides).length} active</li>
        </ul>
      </SectionCard>
      )}
    </div>
  );
}
